import React from 'react';
import { ArrowRight, Building2, ClipboardCheck, Cloud, Code2, Landmark, ShieldCheck, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import Reveal from '../components/Reveal';
import { BtnOutline, BtnPrimary } from '../components/Buttons';

const SOLUTIONS = [
  {
    icon: Workflow,
    title: 'Workflow Automation',
    body: 'Replace manual approvals, fragmented spreadsheets, and repetitive handoffs with secure, auditable workflows that move work forward.',
  },
  {
    icon: Building2,
    title: 'Customer & Operations Portals',
    body: 'Give customers, residents, partners, or internal teams a clear place to submit information, track work, and access the resources they need.',
  },
  {
    icon: Cloud,
    title: 'Modern Cloud Platforms',
    body: 'Plan and build resilient applications, integrations, and cloud infrastructure that can evolve with your organization instead of becoming the next legacy bottleneck.',
  },
  {
    icon: ShieldCheck,
    title: 'Security-Minded Modernization',
    body: 'Modernize systems with role-aware access, defensible architecture, and practical documentation from discovery through deployment.',
  },
];

const PROCESS = [
  ['01', 'Discovery & Scope', 'We clarify the users, workflows, integrations, risks, and definition of a useful first release before committing to a build.'],
  ['02', 'Architecture & Plan', 'We turn the operating problem into a practical technical plan, milestones, and a delivery path your team can understand.'],
  ['03', 'Build & Validate', 'We deliver testable increments, keep communication direct, and validate the experience with the people who will depend on it.'],
  ['04', 'Launch & Improve', 'We document the system, prepare a maintainable release, and create a clear starting point for future improvements.'],
];

const FAQS = [
  ['What does custom software development include?', 'A project can include discovery, web or mobile application development, workflow automation, system integrations, cloud architecture, security controls, testing, deployment support, and documentation. The right mix depends on the operating problem and the people using the system.'],
  ['When is custom software a better fit than off-the-shelf tools?', 'Custom software is most useful when the core workflow, integrations, compliance requirements, or customer experience cannot be handled well by a generic tool without creating costly workarounds. A focused discovery phase should test that assumption before a major build begins.'],
  ['Can Bare Force One work with existing systems?', 'Yes. Many modernization projects begin with existing CRMs, ERPs, payment systems, databases, or vendor platforms. The first step is mapping what should remain, what should connect, and what should be replaced.'],
  ['Do you work with organizations outside Fort Lauderdale?', 'Yes. Bare Force One is based in Fort Lauderdale and supports organizations across Florida and the United States through a documented, collaborative delivery process.'],
];

export default function FortLauderdaleSoftwareDevelopment() {
  return (
    <>
      <SEO
        title="Custom Software Development in Fort Lauderdale, FL"
        description="Bare Force One builds secure custom software, workflow automation, portals, integrations, and cloud platforms for Fort Lauderdale and South Florida organizations."
        path="/fort-lauderdale-custom-software-development"
      />
      <PageHeader
        eyebrow="Fort Lauderdale, Florida"
        title="Custom Software That Moves Your Operations Forward"
        subtitle="Bare Force One helps organizations turn manual, disconnected, or outdated processes into secure software systems built for the way their teams actually work."
      />

      <div className="deferred-content">
      <section className="bg-[#070b16] border-b border-slate-800/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <Reveal direction="right" className="lg:col-span-7">
            <span className="section-number block mb-4">/01</span>
            <h2 className="font-heading font-[800] text-4xl md:text-5xl uppercase tracking-wide text-slate-50 leading-[1.05] mb-6">
              Build Around the Work That <span className="gradient-text">Matters</span>
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed mb-5 max-w-3xl">
              Off-the-shelf software can be a good starting point. But when it forces your people into workarounds, leaves systems disconnected, or cannot support your security and service requirements, it becomes an operational cost.
            </p>
            <p className="text-slate-400 text-base leading-relaxed max-w-3xl">
              We design and build practical web platforms, mobile applications, integrations, and automation for organizations that need reliable systems—not generic features. Every engagement starts with the workflows, users, and decisions the software must improve.
            </p>
          </Reveal>
          <Reveal direction="left" className="lg:col-span-5">
            <div className="glow-card bg-[#0c1222]/60 border border-slate-800/40 p-8">
              <span className="font-heading text-[0.6rem] font-bold uppercase tracking-[0.22em] text-blue-400 block mb-4">A practical first conversation</span>
              <ul className="space-y-4 text-slate-300 text-sm leading-relaxed">
                <li className="flex gap-3"><ClipboardCheck className="text-blue-500 w-5 h-5 shrink-0" />Clarify the problem, users, and operating constraints.</li>
                <li className="flex gap-3"><Code2 className="text-blue-500 w-5 h-5 shrink-0" />Identify the right mix of software, integration, and process change.</li>
                <li className="flex gap-3"><Landmark className="text-blue-500 w-5 h-5 shrink-0" />Map security, compliance, and stakeholder requirements early.</li>
              </ul>
              <div className="mt-8"><BtnPrimary to="/request-proposal">Discuss Your Project <ArrowRight size={14} className="inline ml-1" /></BtnPrimary></div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#090e1a] border-b border-slate-800/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal>
            <span className="section-number block mb-4">/02</span>
            <h2 className="font-heading font-[800] text-4xl md:text-5xl uppercase tracking-wide text-slate-50 leading-[1.05] mb-4">What We Can <span className="gradient-text">Build Together</span></h2>
            <p className="text-slate-400 text-base max-w-2xl leading-relaxed mb-12">A focused software project should create a visible improvement in how your organization serves customers, manages work, or uses information.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SOLUTIONS.map((solution, index) => (
              <Reveal key={solution.title} delay={index * 0.1} direction="up">
                <div className="glow-card bg-[#0c1222]/50 border border-slate-800/40 p-8 h-full">
                  <solution.icon className="text-blue-500 mb-5 w-6 h-6" strokeWidth={1.5} />
                  <h3 className="font-heading font-bold text-xl uppercase tracking-wide text-slate-50 mb-3">{solution.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{solution.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#070b16] border-b border-slate-800/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal>
            <span className="section-number block mb-4">/03</span>
            <h2 className="font-heading font-[800] text-4xl md:text-5xl uppercase tracking-wide text-slate-50 leading-[1.05] mb-12">A Delivery Process Built for <span className="gradient-text">Clarity</span></h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map(([number, title, body], index) => (
              <Reveal key={number} delay={index * 0.1} direction="scale">
                <div className="border-t border-blue-500/40 pt-6 h-full">
                  <span className="font-mono text-xs text-blue-400">{number}</span>
                  <h3 className="font-heading font-bold text-lg uppercase tracking-wide text-slate-50 mt-4 mb-3">{title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#090e1a] border-b border-slate-800/30">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <Reveal>
            <span className="section-number block mb-4">/04</span>
            <h2 className="font-heading font-[800] text-4xl md:text-5xl uppercase tracking-wide text-slate-50 leading-[1.05] mb-10">Questions We Hear <span className="gradient-text">Early</span></h2>
          </Reveal>
          <div className="space-y-5">
            {FAQS.map(([question, answer], index) => (
              <Reveal key={question} delay={index * 0.08} direction="up">
                <article className="glow-card bg-[#0c1222]/50 border border-slate-800/40 p-7">
                  <h3 className="font-heading font-bold text-base uppercase tracking-wide text-slate-50 mb-3">{question}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{answer}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative scanline-overlay bg-[#070b16]">
        <div className="relative z-10 max-w-3xl mx-auto px-6 py-24 lg:py-32 text-center">
          <Reveal direction="scale">
            <h2 className="font-heading font-[800] text-4xl md:text-5xl uppercase tracking-wide text-slate-50 leading-[1.05] mb-6">Ready to Define the <span className="gradient-text">Right Next Step?</span></h2>
            <p className="text-slate-400 text-base leading-relaxed mb-10">Tell us what is slowing your team down. We will help you frame a practical path from current state to a maintainable system.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <BtnPrimary to="/request-proposal">Request a Proposal <ArrowRight size={14} className="inline ml-1" /></BtnPrimary>
              <BtnOutline to="/case-studies">View Case Studies</BtnOutline>
            </div>
            <p className="mt-8 text-slate-500 text-sm">Explore our <Link to="/government" className="text-blue-400 hover:text-blue-300">government</Link>, <Link to="/enterprise" className="text-blue-400 hover:text-blue-300">enterprise</Link>, and <Link to="/local-business" className="text-blue-400 hover:text-blue-300">local-business</Link> service paths.</p>
          </Reveal>
        </div>
      </section>
      </div>
    </>
  );
}
